import React from 'react'
import PropTypes from 'prop-types'
import { injectIntl } from 'react-intl'
import { Flex, Box, Copy, Icon, themeColor, Hide } from '@elparking/components'
import styled from 'styled-components'

import { toDate, makeHourTimeSpan, makeMinuteTimeSpan, maybeParseTimestamp } from 'commons/js/util/dateConverter'

import PlacesSelect from 'parking-search-map/js/ui/PlacesSelect'
import DateTimeSelect from 'parking-search-map/js/ui/DateTimeSelect'
import {
    CALENDAR_DATETIME_FORMAT,
} from 'commons/js/constants'

import SyncBookingDates from 'commons/js/components/SyncBookingDates'
import buildSearch from './buildSearch'
import AirportSelector from './AirportSelector'
import FilterBar from './FilterBar'
import {
    AIRPORT_TYPE,
    POI_TYPE,
    mapPlacePredictionWithType,
    mapAirportPredictionWithType,
    makeAggregatedAutocomplete,
} from 'commons/js/services/autocompleteService'

import messages from './messages'
import { getActiveFilters, isAirportParkingTypeFilterEnabled, isMowizTruckFilterEnabled } from '../../util'
import MowizTruckSwitcher from './MowizTruckSwitcher'

function valueFromPlace (place) {
    if (place.airport_id) {
        return mapAirportPredictionWithType(place).value
    }

    return mapPlacePredictionWithType(place).value
}

export const FILTER_MOWIZTRUCK = 'mowizTruck'
export const FILTER_AIRPORTS = 'airports'
export const FILTER_ALL = 'parkings'

const DatesContainer = styled(Flex)`
    border: 1px solid ${themeColor('gray20')};
    border-radius: 2px;
    background-color: ${themeColor('white')};
    position: relative;
    margin-bottom: 0;
    border-radius: 0px 0px 2px 2px;
`

const PlaceSelectorContainer = styled(DatesContainer)`
    border-bottom: 0px;
    border-radius: 2px 2px 0px 0px;
`

const AirportSelectorContainer = styled(Flex)`
    position: relative;
    width: 70px;
    height: 100%;
    padding: 0;
    margin: 0;
`

const SeparatorBox = styled(Box)`
    height: 100%;
    border-left: 1px solid ${themeColor('gray20')};
    margin-left: -1px;
`

const getStateFromProps = (props) => ({
    place: props.place,
    startDate: props.startDate,
    endDate: props.endDate,
})

class SearchForm extends React.PureComponent {
    static propTypes = {
        intl: PropTypes.object,
        format: PropTypes.string,
        /* eslint-disable react/no-unused-prop-types */
        place: PropTypes.shape({
            place_id: PropTypes.string,
            airport_id: PropTypes.any,
        }),
        startDate: PropTypes.number,
        endDate: PropTypes.number,
        /* eslint-enable react/no-unused-prop-types */
        showGoogleBanner: PropTypes.bool,
        onSearch: PropTypes.func,
        onChangePlace: PropTypes.func,
        onChangeDates: PropTypes.func,
        valueFromPlace: PropTypes.func,
        onShowFilters: PropTypes.func,
        setFilters: PropTypes.func,
        filters: PropTypes.object,
        showFilterBox: PropTypes.bool.isRequired,
        showMowizTruckBox: PropTypes.bool.isRequired,
    }

    static defaultProps = {
        format: CALENDAR_DATETIME_FORMAT,
        place: {},
        onSearch: () => { },
        onChangePlace: () => { },
        onChangeDates: () => { },
        onShowFilters: () => { },
        setFilters: () => { },
        valueFromPlace,
        filters: {},
    }

    state = {
        ...getStateFromProps(this.props),
        prevSearch: getStateFromProps(this.props),
        isValid: null,
    }

    performSearch = this.performSearch.bind(this)
    onChangePlace = this.onChangePlace.bind(this)
    onChangeDates = this.onChangeDates.bind(this)
    renderPlacesSelectOption = this.renderPlacesSelectOption.bind(this)
    selectIconType = this.selectIconType.bind(this)

    performSearch () {
        const search = buildSearch(this.state)
        this.props.onSearch(search)
    }

    static getDerivedStateFromProps (nextProps, { prevSearch }) {
        const search = getStateFromProps(nextProps)
        // TODO: Only update when needed, otherwise PureComponent is useless here
        if ((search.place !== prevSearch.place) ||
            (search.startDate !== prevSearch.startDate) ||
            (search.endDate !== prevSearch.endDate)) {
            return {
                ...search,
                prevSearch: search,
            }
        }
        return null
    }

    onChangePlace ({ value, label }) {
        const search = buildSearch(this.state)
        const place = {
            ...search.place,
            name: label,
            aggregatedId: value,
        }
        this.setState(
            () => ({ place }),
            () => {
                this.props.onSearch({
                    ...search,
                    place,
                })
                this.props.onChangePlace(place)
            }
        )
    }

    onChangeDates ({ startDate, endDate }) {
        this.setState(
            () => ({
                startDate,
                endDate,
            }),
            () => {
                this.performSearch()
                this.props.onChangeDates({ startDate, endDate })
            }
        )
    }

    renderPlacesSelect () {
        const { intl, valueFromPlace, showGoogleBanner } = this.props
        const { place } = this.state
        const value = valueFromPlace(place)
        const placeholder = intl.formatMessage(messages.placePlaceholder)

        return (
            <PlacesSelect
              showGoogleBanner={showGoogleBanner}
              value={{ label: place.name, value }}
              placeholder={placeholder}
              autocompleteService={makeAggregatedAutocomplete({ formatMessage: intl.formatMessage })}
              onChange={this.onChangePlace}
              optionRenderer={this.renderPlacesSelectOption}
            />
        )
    }

    selectIconType (val) {
        const value = val.value || val
        if (value.startsWith(AIRPORT_TYPE)) {
            return 'plane'
        }
        if (value.startsWith(POI_TYPE)) {
            return 'event'
        }
        return 'location'
    }

    renderPlacesSelectOption ({ label, value }) {
        const iconType = this.selectIconType(value)
        return (
            <Flex alignItems='center'>
                <Icon type={iconType} color='gray' align='baseline' /> <Box pl='0.5em'>{label}</Box>
            </Flex>
        )
    }

    renderMobileDateSelect (props, onChange) {
        const { intl, format } = this.props

        return (
            <DateTimeSelect
              value={props.value && toDate(maybeParseTimestamp(props.value))}
              dayPickerProps={{
                    month: props.month,
                    fromMonth: props.fromMonth,
                    disabledDays: props.disabledDays,
                    locale: intl.locale,
                }}
              timePickerProps={{
                    disabledHours: props.disabledHours,
                }}
              onChange={onChange}
              format={format}
            />
        )
    }

    renderEndDateSelect (props, onChange) {
        const { intl, format } = this.props

        return (
            <DateTimeSelect
              dataTest='parking-search-form-date-start'
              ref={(element) => { this.endDateSelect = element }}
              value={props.value && toDate(maybeParseTimestamp(props.value))}
              dayPickerProps={{
                    month: props.month,
                    fromMonth: props.fromMonth,
                    disabledDays: props.disabledDays,
                    locale: intl.locale,
                }}
              timePickerProps={{
                    disabledHours: props.disabledHours,
                }}
              onChange={onChange}
              format={format}
              onNext={() => this.endDateSelect.blur()}
              showNextButton
            />
        )
    }
    renderStartDateSelect (props, onChange) {
        const { intl, format } = this.props

        return (
            <DateTimeSelect
              dataTest='parking-search-form-date-end'
              ref={(element) => { this.startDateSelect = element }}
              value={props.value && toDate(maybeParseTimestamp(props.value))}
              dayPickerProps={{
                    month: props.month,
                    fromMonth: props.fromMonth,
                    disabledDays: props.disabledDays,
                    locale: intl.locale,
                }}
              timePickerProps={{
                    disabledHours: props.disabledHours,
                }}
              onChange={onChange}
              format={format}
              onNext={() => this.endDateSelect.focus()}
              showNextButton
            />
        )
    }

    onChangeAirportFilter (value) {
        const { setFilters } = this.props
        setFilters({
            parkingType: {
                airport: value === FILTER_AIRPORTS,
            },
        })
    }

    onChangeAirportFilter = this.onChangeAirportFilter.bind(this)

    onChangeMowizTruckFilter (value) {
        const { setFilters } = this.props
        setFilters({
            parkingType: {
                onlyMowizTruck: value === FILTER_MOWIZTRUCK,
            },
        })
    }

    onChangeMowizTruckFilter = this.onChangeMowizTruckFilter.bind(this)

    render () {
        const { intl, onShowFilters, filters, showFilterBox, showMowizTruckBox } = this.props
        const filterNumber = getActiveFilters(filters)
        const parkingType = isAirportParkingTypeFilterEnabled(filters) ? FILTER_AIRPORTS : FILTER_ALL
        const onlyMowizTruck = isMowizTruckFilterEnabled(filters)

        return (<div>
            <SyncBookingDates
              startDate={this.props.startDate}
              endDate={this.props.endDate}
              timeSpan={makeHourTimeSpan(1)}
              minTimeSpan={makeMinuteTimeSpan(10)}
              onChange={this.onChangeDates}
            >
                {({ startDateProps, endDateProps, onChangeStart, onChangeEnd }) => (
                    <Flex w='100%' flexWrap={['wrap', 'nowrap']}>
                        <Hide xs sm md w='100%'>
                            <DatesContainer w='100%' flexWrap='wrap'>
                                <Flex flex={['1 1 100%', '1']} flexWrap='wrap'>
                                    <Box w='100%' pl='0.8em' pt='0.5em'>
                                        <Copy as='span' color='gray60' fontWeight='medium' lineHeight='0' size='xxxSmall' transform='uppercase' padding='0'>
                                            {this.props.intl.formatMessage(messages.placeLabel)}
                                        </Copy>
                                    </Box>
                                    <Box w='calc(100% - 2px)'>
                                        {this.renderPlacesSelect()}
                                    </Box>
                                </Flex>
                                <Box flex='0 1 1px'>
                                    <SeparatorBox />
                                </Box>
                                <Flex flex={['1', '0 1 25%']} flexWrap='wrap'>
                                    <Box w='100%' pl='0.8em' pt='0.5em'>
                                        <Copy as='span' color='gray60' fontWeight='medium' lineHeight='0' size='xxxSmall' transform='uppercase' mb='0' padding='0'>
                                            {this.props.intl.formatMessage(messages.startDateLabel)}
                                        </Copy>
                                    </Box>
                                    <Box w='100%' mb='4px'>
                                        {this.renderStartDateSelect(startDateProps, onChangeStart)}
                                    </Box>
                                </Flex>
                                <Flex flex='0 1 1px' alignItems='center'>
                                    <SeparatorBox style={{ height: '1.5em' }} />
                                </Flex>
                                <Flex flex={['1', '0 1 25%']} flexWrap='wrap'>
                                    <Box w='100%' pl='0.8em' pt='0.5em'>
                                        <Copy as='span' color='gray60' fontWeight='medium' lineHeight='0' size='xxxSmall' transform='uppercase' padding='0'>
                                            {this.props.intl.formatMessage(messages.endDateLabel)}
                                        </Copy>
                                    </Box>
                                    <Box w='100%' mb='4px'>
                                        {this.renderEndDateSelect(endDateProps, onChangeEnd)}
                                    </Box>
                                </Flex>
                            </DatesContainer>
                        </Hide>
                        <Box flex='1'>
                            <Hide lg>
                                <PlaceSelectorContainer w='100%' flex='1' flexWrap='nowrap' mb='1em'>
                                    <AirportSelectorContainer w='100%' flex='1' flexWrap='nowrap' mb='1em'>
                                        <AirportSelector
                                          selected={parkingType}
                                          onChange={({ value }) => this.onChangeAirportFilter(value)}
                                          options={[{
                                                dataTest: 'search_all_option',
                                                icon: 'parking-signal',
                                                label: intl.formatMessage(messages.allFilterLabel),
                                                value: FILTER_ALL,
                                                iconSize: 'xxLarge',
                                            }, {
                                                dataTest: 'search_airports_option',
                                                icon: 'plain_line',
                                                label: intl.formatMessage(messages.airportsFilterLabel),
                                                value: FILTER_AIRPORTS,
                                                iconSize: 'medium',
                                            }]}
                                        />
                                    </AirportSelectorContainer>
                                    <Flex flex={['1 1 100%', '1']} flexWrap='wrap'>
                                        <Box w='100%'>
                                            {this.renderPlacesSelect()}
                                        </Box>
                                    </Flex>
                                </PlaceSelectorContainer>
                                <DatesContainer w='100%' flex='1' flexWrap='nowrap'>
                                    <Flex flex={['1', '0 1 50%']} flexWrap='wrap'>
                                        <Box w='100%' pl='1em' pt='0.4em'>
                                            <Copy as='span' color='gray60' fontWeight='medium' lineHeight='0' size='xxxSmall' transform='uppercase'>
                                                {this.props.intl.formatMessage(messages.startDateLabel)}
                                            </Copy>
                                        </Box>
                                        <Box w='100%'>
                                            {this.renderMobileDateSelect(startDateProps, onChangeStart)}
                                        </Box>
                                    </Flex>
                                    <Box flex='0 1 1px' py='0.5em'>
                                        <SeparatorBox />
                                    </Box>
                                    <Flex flex={['1', '0 1 50%']} flexWrap='wrap'>
                                        <Box w='100%' pl='1em' pt='0.4em'>
                                            <Copy as='span' color='gray60' fontWeight='medium' lineHeight='0' size='xxxSmall' transform='uppercase'>
                                                {this.props.intl.formatMessage(messages.endDateLabel)}
                                            </Copy>
                                        </Box>
                                        <Box w='100%'>
                                            {this.renderMobileDateSelect(endDateProps, onChangeEnd)}
                                        </Box>
                                    </Flex>
                                </DatesContainer>
                            </Hide>
                        </Box>
                    </Flex>
                )}
            </SyncBookingDates>
            {showFilterBox &&
                <Hide xs sm md>
                    <Box pt='1em' w='100%'>
                        <FilterBar
                          parkingType={parkingType}
                          onChangeAirportFilter={this.onChangeAirportFilter}
                          filterNumber={filterNumber}
                          onShowFilters={onShowFilters}
                        />
                    </Box>
                </Hide>
            }
            {showMowizTruckBox && <MowizTruckSwitcher
              selected={onlyMowizTruck}
              onChange={this.onChangeMowizTruckFilter}
            />}
        </div>)
    }
}

export default injectIntl(SearchForm)
